import * as React from "react";
// react-day-picker's own locale, not date-fns's: the same date-fns locale plus the
// calendar's aria labels (month and year lists, the arrows, each day) in that
// language. The bare date-fns `et` left them in English in an Estonian UI.
import { et } from "react-day-picker/locale";
import { Clock } from "lucide-react";
import { Calendar } from "./calendar";
import { Input } from "./input";
import { Label } from "./label";
import { cn } from "../lib/utils";

export interface DateTimePanelProps {
  /** Full ISO datetime string (e.g. `2026-06-12T14:30:00.000Z`). Empty = unset. */
  value?: string;
  onChange: (v: string) => void;
  /** Display locale ("en" | "et"). Affects the weekday names + week start. */
  locale?: string;
  /** Hour used when a day is picked while nothing is set yet. */
  defaultHour?: number;
  /** Move keyboard focus into the calendar on mount (the popover variant does). */
  autoFocus?: boolean;
  className?: string;
  /** Rendered under the time row - e.g. a "Done" button in the popover variant. */
  footer?: React.ReactNode;
  /**
   * Turn the month caption into month + year dropdowns.
   *
   * Off by default, because for most of this platform's date fields the month
   * arrows are the whole journey - a task due next week is never a year away,
   * and two selects would be two controls where a label is enough. Turn it on
   * where the target genuinely can be far from today.
   *
   * The year list is bounded by `yearsBack` / `yearsAhead` below. Without a
   * bound react-day-picker offers a century, which is a scroll, not a choice.
   */
  withYearPicker?: boolean;
  /** How many years before this one the dropdown offers. Default 2. */
  yearsBack?: number;
  /** How many years after this one the dropdown offers. Default 5. */
  yearsAhead?: number;
}

/**
 * The date + time surface itself: a react-day-picker calendar plus HH/MM inputs,
 * with NO popover of its own.
 *
 * Split out of `DateTimePicker` so a caller that already owns a surface (a dialog, a
 * settings panel) can show the calendar directly instead of hiding it behind a second
 * trigger inside a layer it already opened. `DateTimePicker` is this same panel wrapped
 * in a popover, for ordinary forms.
 */
export function DateTimePanel({
  value, onChange, locale = "et", defaultHour = 9, autoFocus, className, footer,
  withYearPicker, yearsBack = 2, yearsAhead = 5,
}: DateTimePanelProps) {
  // Accept any Estonian locale tag ("et", "et-EE", …) - callers pass either.
  const isEt = (locale ?? "").toLowerCase().startsWith("et");

  const date: Date | undefined = React.useMemo(() => {
    if (!value) return undefined;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? undefined : d;
  }, [value]);

  /**
   * The year window the dropdown offers, widened to contain whatever is already
   * selected.
   *
   * Without the widening a value outside the default window - an old imported
   * record, a contract that runs long - would be unreachable: the calendar
   * cannot navigate to a month outside its bounds, so the field would show a
   * date the picker refuses to display, and the operator would have no way back
   * to it after one stray click.
   */
  const thisYear = new Date().getFullYear();
  const selectedYear = date?.getFullYear() ?? thisYear;
  const firstYear = Math.min(thisYear - yearsBack, selectedYear);
  const lastYear = Math.max(thisYear + yearsAhead, selectedYear);

  function handleDayClick(day: Date) {
    const h = date ? date.getHours()   : defaultHour;
    const m = date ? date.getMinutes() : 0;
    onChange(new Date(day.getFullYear(), day.getMonth(), day.getDate(), h, m, 0, 0).toISOString());
  }

  function handleTimeChange(part: "h" | "m", raw: string) {
    let n = parseInt(raw, 10);
    if (!Number.isFinite(n)) n = 0;
    n = part === "h" ? Math.max(0, Math.min(23, n)) : Math.max(0, Math.min(59, n));
    const base = date ? new Date(date) : (() => { const d = new Date(); d.setSeconds(0, 0); return d; })();
    if (part === "h") base.setHours(n);
    else              base.setMinutes(n);
    onChange(base.toISOString());
  }

  return (
    <div className={cn("flex flex-col", className)}>
      <Calendar
        mode="single"
        selected={date}
        // `defaultMonth`, NOT `month`: the latter makes the displayed month controlled,
        // and without an onMonthChange the prev/next arrows silently do nothing - you
        // could never schedule into another month.
        defaultMonth={date}
        onDayClick={handleDayClick}
        locale={isEt ? et : undefined}
        captionLayout={withYearPicker ? "dropdown" : "label"}
        // Bounds only when the dropdowns are actually drawn. Setting them under
        // the label caption would silently disable the prev/next arrows outside
        // the window, which is a different feature nobody asked for.
        {...(withYearPicker
          ? { startMonth: new Date(firstYear, 0, 1), endMonth: new Date(lastYear, 11, 31) }
          : {})}
        autoFocus={autoFocus}
      />
      <div className="border-t px-4 py-3 flex flex-col gap-3 bg-muted/30">
        <div className="flex items-center justify-between gap-3">
          <div className="inline-flex items-center gap-1.5">
            <Clock className="size-4 text-muted-foreground" />
            <Label className="text-xs text-muted-foreground">{isEt ? "Kellaaeg" : "Time"}</Label>
          </div>
          <div className="inline-flex items-center gap-1.5">
            <Input
              type="number" min={0} max={23}
              value={String(date ? date.getHours() : defaultHour).padStart(2, "0")}
              onChange={(e) => handleTimeChange("h", e.target.value)}
              className="w-16 h-9 text-center text-base font-bold tabular-nums"
            />
            <span className="text-muted-foreground font-bold text-lg">:</span>
            <Input
              type="number" min={0} max={59}
              value={String(date ? date.getMinutes() : 0).padStart(2, "0")}
              onChange={(e) => handleTimeChange("m", e.target.value)}
              className="w-16 h-9 text-center text-base font-bold tabular-nums"
            />
          </div>
        </div>
        {footer}
      </div>
    </div>
  );
}
